import { useState } from 'react';
import { useAuth } from '@/lib/auth/react';
import { finishSignupImperative } from '@/store/signupProgressStore';
import facebookIcon from '@/assets/settings/social-facebook.svg';
import instagramIcon from '@/assets/settings/social-instagram.svg';
import xIcon from '@/assets/settings/social-x.svg';

// TODO(boss): real social profile URLs — placeholders until marketing supplies them.
const SOCIAL_LINKS: { label: string; icon: string; href: string }[] = [
  { label: 'Facebook', icon: facebookIcon, href: '#' },
  { label: 'Instagram', icon: instagramIcon, href: '#' },
  { label: 'X', icon: xIcon, href: '#' },
];

// Placeholder legal URL — same convention as SignupStepTerms (no real page yet).
const PRIVACY_URL = '/cdna-privacy-policy';

/**
 * Settings page footer (Figma "Footer"): social icons plus underlined
 * "Log out" / "Privacy policy" links. Logout drops the stale signup-resume
 * flag, then hard-navigates to the lander: the full refresh resets every
 * in-memory state (React Query caches, zustand stores, Intercom/LD identities)
 * instead of trusting each consumer to reconcile a live auth flip. Navigating
 * to `/` rather than reloading in place keeps the signed-out user off the
 * protected settings route, which would auto-pop the login modal.
 */
export function SettingsFooter() {
  const { logout } = useAuth();
  const [pending, setPending] = useState(false);

  async function handleLogout() {
    if (pending) return;
    setPending(true);
    try {
      await logout();
      finishSignupImperative();
      window.location.assign('/');
    } finally {
      setPending(false);
    }
  }

  return (
    <footer className="flex flex-col items-center gap-6 pb-8 pt-10">
      <div className="flex items-center justify-center gap-6">
        {SOCIAL_LINKS.map(({ label, icon, href }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={label}
            className="drop-shadow-[0px_4px_2px_rgba(0,0,0,0.25)] transition-opacity hover:opacity-85"
          >
            <img src={icon} alt="" className="size-10" />
          </a>
        ))}
      </div>
      <div className="flex items-center justify-center gap-8">
        <button
          type="button"
          onClick={() => void handleLogout()}
          disabled={pending}
          data-testid="settings-logout"
          className="text-lg text-purple underline transition-opacity hover:opacity-85 disabled:opacity-50"
        >
          {pending ? 'Signing out…' : 'Log out'}
        </button>
        <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="text-lg text-purple underline">
          Privacy policy
        </a>
      </div>
    </footer>
  );
}
